import { useEffect, useState } from "react";
import ModalShell, { modalKeyValue, modalPrimaryButton } from "./ModalShell";
import { createShare, emailShare, formatCode, myShares, revokeShare } from "./shareApi";

/**
 * 분류 결과 공유 창(2026-10-06, 1단계) — 링크 만들기 → 복사 · 메일 · 휴대폰 공유. 보낸 링크는 아래 목록에서 끊는다.
 * 받는 사람은 로그인 없이 /s/토큰 으로 본다(SharedResultPage).
 */
const btn = { border: "1px solid var(--audit-border)", background: "transparent", borderRadius: 8, padding: "6px 12px", fontSize: 13, cursor: "pointer", color: "var(--audit-text)" };
const input = { width: "100%", boxSizing: "border-box", border: "1px solid var(--audit-border)", borderRadius: 8, padding: "7px 10px", font: "inherit", fontSize: 13, background: "var(--audit-bg-elevated, #fff)", color: "var(--audit-text)" };
const label = { fontSize: 12, color: "var(--audit-text-muted)", display: "block", marginBottom: 4 };

export default function ShareDialog({ snapshot, onClose, fetchImpl = fetch }) {
  const [days, setDays] = useState(30);
  const [note, setNote] = useState("");
  const [share, setShare] = useState(null);       // {token, url, expires_at}
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [copied, setCopied] = useState(false);
  const [to, setTo] = useState("");
  const [mailMsg, setMailMsg] = useState("");
  const [list, setList] = useState(null);
  const canNativeShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

  const loadList = () => myShares(fetchImpl).then((j) => setList(j.items || [])).catch(() => setList([]));
  useEffect(() => { loadList(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const make = async () => {
    setBusy(true); setMsg("");
    try {
      const j = await createShare({ ...snapshot, note, days }, fetchImpl);
      setShare(j);
      loadList();
    } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(share.url); setCopied(true); } catch { setMsg("복사하지 못했습니다. 주소를 직접 선택해 복사해 주세요."); }
  };

  const sendMail = async () => {
    setBusy(true); setMailMsg("");
    try {
      const j = await emailShare(share.token, to, note, fetchImpl);
      setMailMsg(j.failed?.length ? `보냄 ${j.sent.length}명 · 못 보냄: ${j.failed.join(", ")}` : `${j.sent.length}명에게 보냈습니다.`);
      if (!j.failed?.length) setTo("");
      loadList();
    } catch (e) { setMailMsg(e.message); } finally { setBusy(false); }
  };

  const nativeShare = async () => {
    try {
      await navigator.share({ title: "LoginHS 세번 분류 결과", text: `${snapshot.query || formatCode(snapshot.code)} — ${formatCode(snapshot.code)}`, url: share.url });
    } catch { /* 사용자가 닫음 */ }
  };

  const revoke = async (t) => { try { await revokeShare(t, fetchImpl); loadList(); if (share?.token === t) setShare(null); } catch (e) { setMsg(e.message); } };

  return (
    <ModalShell title="결과 공유" subtitle="받는 사람은 로그인 없이 브라우저로 봅니다 · 제안이며 확정 아님" onClose={onClose} maxWidth={520} testId="share-dialog">
      <div style={{ display: "grid", gap: 14 }}>
        <div style={{ fontSize: 13 }}>
          <span style={{ color: "var(--audit-text-muted)" }}>공유할 결과 </span>
          {snapshot.code && <span style={{ ...modalKeyValue, fontFamily: "Consolas, monospace" }}>{formatCode(snapshot.code)}</span>}
          {snapshot.query && <span style={{ marginLeft: 8 }}>{snapshot.query}</span>}
          <div style={{ fontSize: 12, color: "var(--audit-text-muted)", marginTop: 2 }}>출처별 세번 {snapshot.sources?.length || 0}줄과 세율 요약이 함께 갑니다. 관세사 이력은 비율만, 메모·첨부는 가지 않습니다.</div>
        </div>

        {!share ? (
          <>
            <div>
              <span style={label}>받는 사람에게 남길 말(선택)</span>
              <input style={input} value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} placeholder="예: 다음 주 수입분 이 세번으로 진행 예정입니다" aria-label="남길 말" />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span style={label}>링크 열리는 기간</span>
              {[7, 30, 90].map((d) => (
                <label key={d} style={{ fontSize: 13, display: "inline-flex", gap: 4, alignItems: "center" }}>
                  <input type="radio" name="share-days" checked={days === d} onChange={() => setDays(d)} /> {d}일
                </label>
              ))}
            </div>
            <div><button type="button" style={modalPrimaryButton} onClick={make} disabled={busy} data-testid="share-make">{busy ? "만드는 중…" : "공유 링크 만들기"}</button></div>
          </>
        ) : (
          <>
            <div>
              <span style={label}>공유 링크 · {String(share.expires_at).slice(0, 10)} 까지</span>
              <div style={{ display: "flex", gap: 6 }}>
                <input style={{ ...input, fontFamily: "Consolas, monospace" }} readOnly value={share.url} onFocus={(e) => e.target.select()} aria-label="공유 링크" data-testid="share-url" />
                <button type="button" style={modalPrimaryButton} onClick={copy}>{copied ? "복사됨" : "복사"}</button>
              </div>
              <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                <a href={share.url} target="_blank" rel="noreferrer" style={{ ...btn, textDecoration: "none" }}>열어 보기 ↗</a>
                {canNativeShare && <button type="button" style={btn} onClick={nativeShare}>휴대폰으로 공유(카톡·문자)</button>}
              </div>
            </div>
            <div>
              <span style={label}>메일로 보내기 · 한 번에 5명</span>
              <div style={{ display: "flex", gap: 6 }}>
                <input style={input} value={to} onChange={(e) => setTo(e.target.value)} placeholder="받는 사람 메일 (쉼표로 여러 명)" aria-label="받는 사람 메일" data-testid="share-mail-to" />
                <button type="button" style={btn} onClick={sendMail} disabled={busy || !to.trim()} data-testid="share-mail-send">보내기</button>
              </div>
              {mailMsg && <div style={{ fontSize: 12, marginTop: 4 }} role="status">{mailMsg}</div>}
            </div>
          </>
        )}
        {msg && <div style={{ fontSize: 12 }} role="alert">{msg}</div>}

        {list && list.length > 0 && (
          <div>
            <span style={label}>내가 만든 공유</span>
            <div style={{ display: "grid", gap: 2, maxHeight: 160, overflow: "auto" }}>
              {list.map((s) => {
                const dead = s.revoked || s.expired;
                return (
                  <div key={s.token} style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, fontSize: 12, padding: "4px 0", borderBottom: "1px solid var(--audit-border)", opacity: dead ? 0.55 : 1 }}>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      <b style={{ fontFamily: "Consolas, monospace" }}>{formatCode(s.code)}</b> {s.query} · 열람 {s.views}{s.emails ? ` · 메일 ${s.emails}` : ""} · {dead ? (s.revoked ? "끊음" : "만료") : `${String(s.expires_at).slice(0, 10)}까지`}
                    </span>
                    {!dead && <button type="button" style={{ ...btn, padding: "1px 8px", fontSize: 12 }} onClick={() => revoke(s.token)}>끊기</button>}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </ModalShell>
  );
}
